import { useEffect, useState } from 'react'

import { cloneDeep, set } from 'lodash'
import { ArrowDown, ArrowDownLeft, ArrowDownRight, ArrowLeft, ArrowRight, ArrowUp, ArrowUpLeft, ArrowUpRight, EllipsisVertical } from 'lucide-react'
import { ToggleOptionGroup, ToggleOptionGroupItem, Select, Input, RotateDegrees, DefaultTooltip } from 'twyne-new.ui'

const PickerConfig = ({ gradient, onChange }) => {
  const handleChange = (value, key) => {
    const gradientClone = cloneDeep(gradient)
    onChange(set(gradientClone, key, value))
  }

  return <>
    <ToggleOptionGroup className='mb-4' type='single' size='lg' value={gradient?.type || 'linear'} onValueChange={type => type && handleChange(type, 'type')}>
      <ToggleOptionGroupItem value='linear'>
        Linear
      </ToggleOptionGroupItem>
      <ToggleOptionGroupItem value='conic'>
        Conic
      </ToggleOptionGroupItem>
      <ToggleOptionGroupItem value='radial'>
        Radial
      </ToggleOptionGroupItem>
    </ToggleOptionGroup>
    <Radial type={gradient?.type} radial={gradient?.radial} onChange={handleChange} />
    <Linear type={gradient?.type} linear={gradient?.linear} onChange={handleChange} />
  </>
}

const Linear = ({ type, linear = {}, onChange }) => {
  const [arbitrary, setArbitrary] = useState()

  useEffect(() => {
    if (arbitrary === undefined) {
      setArbitrary(linear?.degrees !== undefined)
    }
  }, [linear?.degrees, arbitrary])

  const handleChangeArbitrary = value => {
    setArbitrary(value === 'arbitrary')
    onChange({}, 'linear')
  }

  if (type !== 'linear') {
    return null
  }

  return <div className='flex items-center gap-12 justify-center'>
    {!arbitrary ? <>
      <ToggleOptionGroup className='flex justify-between' type='single' value={linear.direction} onValueChange={value => onChange(value, 'linear.direction')}>
        {LINEAR_DIRECTIONS.map(({ value, icon: Icon }) => <>
          <ToggleOptionGroupItem value={value}>
            <Icon size='14' />
          </ToggleOptionGroupItem>
        </>)}
      </ToggleOptionGroup>
    </> : <>
      <RotateDegrees
        value={linear.degrees}
        onChange={degrees => onChange(degrees, 'linear.degrees')}
        style={{
          thumbSize: 3,
          circleSize: 20,
          translateY: -2.3,
        }}
      />
      <Input
        type='number'
        value={linear.degrees}
        onChange={e => onChange(e.target.value ? Number(e.target.value) : '', 'linear.degrees')}
        className='w-12'
        variant='accent'
        min={0}
        max={360}
      />
    </>}

    <ToggleOptionGroup type='single' value={arbitrary ? 'arbitrary' : ''} onValueChange={handleChangeArbitrary}>
      <ToggleOptionGroupItem value='arbitrary'>
        <DefaultTooltip content={arbitrary ? 'Show predefined options' : 'Show degrees picker'} triggerProps={{ className: 'w-full flex justify-center' }}>
          <EllipsisVertical size='14' />
        </DefaultTooltip>
      </ToggleOptionGroupItem>
    </ToggleOptionGroup>
  </div>
}

const Radial = ({ type, radial = {}, onChange }) => {
  if (type !== 'radial') {
    return null
  }

  return <div className='flex gap-2'>
    <Select
      value={radial?.shape}
      onChange={option => onChange(option?.value, 'radial.shape')}
      placeholder='Shape'
      options={RADIAL_SHAPE}
      clearable
    />

    <Select
      value={radial?.size}
      onChange={option => onChange(option?.value, 'radial.size')}
      clearable
      placeholder='Size'
      options={radial?.shape === 'circle' ? RADIAL_CIRCLE : RADIAL_ELLIPSE}
    />
  </div>
}

const RADIAL_SHAPE = [
  { value: 'circle', label: 'Circle' },
  { value: 'ellipse', label: 'Ellipse' },
]

const RADIAL_CIRCLE = [
  { label: 'Closest Side', value: 'closest-side' },
  { label: 'Farthest Side', value: 'farthest-side' },
]

const RADIAL_ELLIPSE = [
  { label: 'Closest Side', value: 'closest-side' },
  { label: 'Closest Corner', value: 'closest-corner' },
  { label: 'Farthest Side', value: 'farthest-side' },
  { label: 'Farthest Corner', value: 'farthest-corner' },
]

const LINEAR_DIRECTIONS = [
  { value: 'to_top', icon: ArrowUp },
  { value: 'to_top_right', icon: ArrowUpRight },
  { value: 'to_right', icon: ArrowRight },
  { value: 'to_bottom_right', icon: ArrowDownRight },
  { value: 'to_bottom', icon: ArrowDown },
  { value: 'to_bottom_left', icon: ArrowDownLeft },
  { value: 'to_left', icon: ArrowLeft },
  { value: 'to_top_left', icon: ArrowUpLeft },
]

export default PickerConfig
